// One period's statement, laid out the way the invoice will lay it out.
//
// A statement is not stored. It is the deal's term lines interpreted over the
// period's facts, so the figures here move until the period is issued — and
// then they stop, because issuing snapshots exactly these columns onto the
// document. Drawing them in the invoice's own order is the point: a person
// checking a draft against this panel is comparing like with like, not
// re-deriving anything.
//
// EVERY LINE SAYS HOW IT REACHED ITS FIGURE. `basis` is the server's own
// sentence and it sits under the line's name; `figure_source` says which of the
// five arms supplied the money. Both are cheap to read and they are what turns
// "USD 329.60" into a figure somebody can agree with.
//
// AN UNPRICED COUNT IS A STATED FIGURE, NEVER AN ABSENCE. A selector that
// refuses 42 calls reports 42, so "we do not bill auto-transfers" reads as a
// number on the page rather than as a gap in a total. The same rule covers a
// missing rate and a rule nobody has written: the line is short by a stated
// count, and it is never short by zero.
//
// THE QUANTITY AND THE UNIT PRICE WILL NOT ALWAYS MULTIPLY OUT TO THE GROSS,
// and the panel says so rather than let a reader think one of the three is
// wrong. A unit price is a decimal of up to nine places — an input token costs
// a small fraction of one cent — and the gross is rounded ONCE, after the
// multiplication over every fact. Printing the exact price beside a rounded
// total is the honest pair; printing a price that multiplied out exactly would
// mean rounding the price, which is the figure that must not move.
//
// THE TWO PRICE ARMS ARE COUNTED SEPARATELY, NEVER SUMMED. A usage fact is
// priced by the provider's own cost fact where one covers its day, and by our
// rate card where none does. "The provider told us" and "we guessed from our
// own list" are different claims about the same money, so a line that used both
// says how many facts each arm answered rather than one number that means
// neither.
//
// THE SCOPE IS WHY A FIGURE IS EMPTY. A deal reads facts only from the
// (deployment, tenant) pairs its own bindings name, so an empty scope is not a
// quiet month — it is a binding nobody wrote. The panel names the pairs it
// read, and says outright when it read none.
//
// A LINE WITH NO FIGURE CARRIES THE CONTROL THAT SUPPLIES ONE. It is the same
// dialog the close screen opens, because a fact is one shape whoever records
// it.

import type { ReactNode } from "react";
import type {
  Currency,
  DealStatement,
  DealStatementLine,
  DealStatementPrepaid,
  DealStatementPriceArms,
  DealStatementScope,
  MeterUnit,
} from "@crm/shared";
import styles from "./StatementPanel.module.css";
import { Badge } from "../../design/Badge.js";
import { Card, CardHeader } from "../../design/Card.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { unpricedNote } from "../../design/FigureProvenance.js";
import { factSourceLabel, figureSourceLabel } from "../../format/labels.js";
import { formatCount, formatMoney, formatQuantity, formatUnitPrice } from "../../format/money.js";
import { formatInstantDate, formatMonth } from "../../format/calendarDate.js";

export interface StatementPanelProps {
  statement: DealStatement;
  /** Null for a reader who may not record a fact. */
  onRecordFact: ((line: DealStatementLine) => void) | null;
  /** A line the form is trying out, drawn beside the saved ones. */
  preview?: DealStatementLine | undefined;
}

/**
 * Where this month stands, in this system's own vocabulary.
 *
 * CLOSED MEANS BILLED HERE, and the panel used to say a month was closed on the
 * strength of `period_open` alone — which says only that the month has ENDED.
 * A month that ended with no document raised for it is the one month with work
 * outstanding, and calling it closed hid exactly that work.
 *
 * A line reads `issued` when a non-void, non-draft invoice froze it (the server
 * excludes both), so the lines themselves say whether a document carries this
 * period, and the invoice numbers on them say which.
 */
function periodStanding(statement: DealStatement): string {
  if (statement.provenance.period_open) {
    return "This period is still open, so these figures are what the facts say so far.";
  }

  const issued = statement.lines.filter((line) => line.status === "issued");
  if (issued.length === 0) return "This month has ended. Nothing is issued for it yet.";

  const numbers = [
    ...new Set(issued.map((line) => line.invoice_number).filter((number) => number !== null)),
  ];
  if (numbers.length === 0) {
    return "Issued on a document with no number recorded. These figures are frozen.";
  }
  return `Issued on invoice ${numbers.join(", ")}. These figures are frozen.`;
}

export function StatementPanel({
  statement,
  onRecordFact,
  preview,
}: StatementPanelProps): ReactNode {
  const currency = statement.currency;
  const total = statement.totals.find((entry) => entry.currency === currency);

  return (
    <Card>
      <CardHeader
        title={`Statement · ${formatMonth(statement.period)}`}
        description={periodStanding(statement)}
        actions={
          total === undefined ? undefined : (
            <span className={styles.total}>
              {formatMoney(total.charged_minor, currency)}
              {total.skipped_lines > 0 && (
                <span className={styles.totalNote}>
                  {`${formatCount(total.skipped_lines)} line${
                    total.skipped_lines === 1 ? "" : "s"
                  } left out — no figure`}
                </span>
              )}
            </span>
          )
        }
      />
      <Table label={`Statement lines for ${formatMonth(statement.period)}`}>
        <thead>
          <tr>
            <Th>Line</Th>
            <Th numeric>Quantity</Th>
            <Th>Unit</Th>
            <Th numeric>Unit price</Th>
            <Th numeric>Gross</Th>
            <Th numeric>Share</Th>
            <Th numeric>Charged</Th>
          </tr>
        </thead>
        <tbody>
          {statement.lines.length === 0 && preview === undefined ? (
            <EmptyRow
              colSpan={7}
              title={`Nothing bills for ${formatMonth(statement.period)}`}
              description="No term line covers this period. A line bills a period when the period falls inside its window and its cadence lands on it."
            />
          ) : (
            <>
              {statement.lines.map((line) => (
                <StatementRow
                  key={`${line.term_line_id}-${line.milestone_seq ?? "-"}`}
                  line={line}
                  currency={currency}
                  onRecordFact={onRecordFact}
                />
              ))}
              {preview !== undefined && (
                <StatementRow line={preview} currency={currency} onRecordFact={null} isPreview />
              )}
            </>
          )}
        </tbody>
      </Table>
      <div className={styles.footnote}>
        {`${currency} · pre-tax · derived ${formatInstantDate(statement.provenance.derived_as_of)}`}
        {statement.provenance.facts_max_received_at !== null &&
          ` · newest fact ${formatInstantDate(statement.provenance.facts_max_received_at)}`}
        {
          " · a quantity times a unit price will not always print out to the gross: the gross is rounded once, after the multiplication."
        }
      </div>
      <ScopeNote scope={statement.provenance.scope} />
    </Card>
  );
}

function StatementRow({
  line,
  currency,
  onRecordFact,
  isPreview = false,
}: {
  line: DealStatementLine;
  currency: Currency;
  onRecordFact: ((line: DealStatementLine) => void) | null;
  isPreview?: boolean;
}): ReactNode {
  const counts = unpricedNote(line.unpriced);

  return (
    <tr className={isPreview ? styles.previewRow : undefined}>
      <Td strong>
        <span className={styles.lineName}>
          {line.label}
          {isPreview && (
            <Badge shape="tag" tone="info">
              Preview
            </Badge>
          )}
          {line.status === "issued" && (
            <Badge shape="tag" tone="slate">
              {line.invoice_number === null ? "Issued" : line.invoice_number}
            </Badge>
          )}
        </span>
        {/* The server's own sentence for how this figure was reached. It is the
            same sentence the month close carries, so the two screens cannot
            explain one figure two ways. */}
        <span className={styles.basis}>{line.basis}</span>
        <span
          className={styles.source}
        >{`Priced by ${figureSourceLabel(line.figure_source)}`}</span>
        {priceArmsNote(line.price_arms) !== null && (
          <span className={styles.source}>{priceArmsNote(line.price_arms)}</span>
        )}
        {/* A fact two clauses both match is a decision somebody made — a deal
            that really does sell two products one call buys — so it stays
            visible on every statement, not only on the day it was made. */}
        {line.stacked_fact_count > 0 && (
          <span className={styles.source}>
            {`${formatCount(line.stacked_fact_count)} fact${
              line.stacked_fact_count === 1 ? "" : "s"
            } also bill another clause on this deal`}
          </span>
        )}
        {counts !== null && <span className={styles.unpriced}>{counts}</span>}
        {line.prepaid !== null && <PrepaidPosition prepaid={line.prepaid} unit={line.unit} />}
      </Td>
      <Td numeric mono>
        {line.quantity === null ? "—" : formatQuantity(line.quantity)}
      </Td>
      <Td muted>{line.unit}</Td>
      <Td numeric mono muted>
        {line.unit_price_minor === null
          ? "—"
          : formatUnitPrice(line.unit_price_minor, currency, { omitCurrencyCode: true })}
      </Td>
      <Td numeric mono muted>
        {line.gross_minor === null ? (
          <NoFigure line={line} onRecordFact={onRecordFact} />
        ) : (
          formatMoney(line.gross_minor, currency, { omitCurrencyCode: true })
        )}
      </Td>
      <Td numeric mono muted>
        {line.share_pct === null ? "—" : `${line.share_pct}%`}
      </Td>
      <Td numeric mono strong>
        {line.charged_minor === null
          ? "—"
          : formatMoney(line.charged_minor, currency, { omitCurrencyCode: true })}
      </Td>
    </tr>
  );
}

/**
 * The empty slot, as a control.
 *
 * Drawn as the field it is about to become rather than as a button beside one,
 * because what a reader is looking for in this column is a NUMBER, and the
 * honest way to say one is missing is a box the shape of it with nothing in it.
 */
function NoFigure({
  line,
  onRecordFact,
}: {
  line: DealStatementLine;
  onRecordFact: ((line: DealStatementLine) => void) | null;
}): ReactNode {
  if (onRecordFact === null) return <span className={styles.unknown}>no figure</span>;
  return (
    <button
      type="button"
      className={styles.slot}
      onClick={() => onRecordFact(line)}
      aria-label={`Record a fact for ${line.label}`}
    >
      <span className={styles.slotField} aria-hidden="true" />
      <span className={styles.slotUnit}>{line.unit}</span>
    </button>
  );
}

/**
 * Where a prepaid block stands, with the opening balance broken into its parts.
 *
 * THE THREE PARTS ARE THREE LINES, and that is the whole reason the shape is
 * this wide. The block is what the paper sold. The carried-in quantity is
 * COMPUTED from the earlier periods' facts. An adjustment is what somebody
 * STATED — a credit, a goodwill top-up, a correction — and it carries the note
 * that says why. Adding a stated figure into a computed one would produce an
 * opening balance nobody can argue with and nobody can check.
 *
 * A signed adjustment reads with its sign, so a deduction is visibly a
 * deduction rather than a smaller number than last month.
 */
function PrepaidPosition({
  prepaid,
  unit,
}: {
  prepaid: DealStatementPrepaid;
  unit: MeterUnit;
}): ReactNode {
  return (
    <span className={styles.prepaid}>
      <span className={styles.prepaidHead}>
        {`Prepaid: ${formatQuantity(prepaid.consumed_quantity)} of ` +
          `${formatQuantity(prepaid.opening_quantity)} ${unit} used · ` +
          `${formatQuantity(prepaid.allocated_quantity)} allocated here · ` +
          (prepaid.overage_quantity > 0
            ? `${formatQuantity(prepaid.overage_quantity)} over`
            : `${formatQuantity(prepaid.remaining_quantity)} left`)}
      </span>
      <span className={styles.prepaidPart}>
        {`${formatQuantity(prepaid.entitlement_quantity)} bought`}
      </span>
      <span className={styles.prepaidPart}>
        {`${formatQuantity(prepaid.carried_in_quantity)} carried in` +
          (prepaid.carry_over === "none" ? " · this block does not roll forward" : "")}
      </span>
      {prepaid.adjustments.length === 0 ? (
        <span className={styles.prepaidPart}>nothing adjusted</span>
      ) : (
        prepaid.adjustments.map((adjustment, index) => (
          <span key={`${adjustment.recorded_by}-${String(index)}`} className={styles.prepaidPart}>
            {`${signed(adjustment.quantity)} adjusted · ` +
              `${factSourceLabel(adjustment.source).toLowerCase()}` +
              (adjustment.note === null ? "" : ` — ${adjustment.note}`)}
          </span>
        ))
      )}
    </span>
  );
}

/** A signed quantity, so a deduction reads as one rather than as a smaller sum. */
export function signed(quantity: number): string {
  return quantity < 0 ? `−${formatQuantity(-quantity)}` : `+${formatQuantity(quantity)}`;
}

/**
 * Which facts this statement was allowed to read.
 *
 * A deal reads facts ONLY from the (deployment, tenant) pairs its own bindings
 * name, per day. So an empty scope is the difference between "a quiet month"
 * and "nobody wrote the binding" — and it is the second far more often. Said
 * out loud rather than left for a reader to infer from an empty table.
 */
function ScopeNote({ scope }: { scope: readonly DealStatementScope[] }): ReactNode {
  if (scope.length === 0) {
    return (
      <div className={styles.scopeEmpty}>
        No tenant binding covers this period, so this statement read no facts at all. Every figure
        it derives is empty because nothing was in scope, not because nothing happened.
      </div>
    );
  }
  return (
    <div className={styles.footnote}>
      {`Read from ${scope
        .map((pair) => `${pair.tenant_slug} on ${pair.deployment_slug}`)
        .join(", ")}`}
    </div>
  );
}

/**
 * How many facts each price arm answered, or null when only one did.
 *
 * Silent on the ordinary line, which one arm priced in full — a mark on the
 * ordinary case is a mark that stops meaning anything. It speaks up for the
 * line that used both, which is the line whose figure a reader most needs to
 * be able to take apart.
 */
function priceArmsNote(arms: DealStatementPriceArms): string | null {
  const parts: string[] = [];
  if (arms.provider_cost > 0) {
    parts.push(`${formatCount(arms.provider_cost)} by the provider's own figure`);
  }
  if (arms.rate_card > 0) parts.push(`${formatCount(arms.rate_card)} by our rate card`);
  if (arms.contract > 0) parts.push(`${formatCount(arms.contract)} by the contract`);
  return parts.length < 2 ? null : `Priced ${parts.join(", ")}`;
}
